import { Link, useLocation } from 'react-router-dom';
import { useLanguage, LANGUAGES } from '@/context/LanguageContext';
import { useInquiryList } from '@/context/InquiryListContext';
import { translations } from '@/data/translations';
import { Menu, ChevronDown, ShoppingBag } from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export function Header() {
  const { language, setLanguage, t } = useLanguage();
  const { itemCount, openDrawer } = useInquiryList();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t(translations.nav.home) },
    { to: '/catalog', label: t(translations.nav.catalog) },
    { to: '/craft', label: t(translations.nav.craft) },
    { to: '/contact', label: t(translations.nav.contact) },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#F9F8F6]/95 backdrop-blur-md border-b border-black/5 shadow-sm' 
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-serif text-xl md:text-2xl tracking-tight text-[#2C2C2C] hover:text-[#4A5D4F] transition-colors"
            data-testid="logo-link"
          >
            Helmut Santifaller
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`font-sans text-sm uppercase tracking-widest transition-colors ${
                  isActive(link.to) 
                    ? 'text-[#8B5A2B]' 
                    : 'text-[#2C2C2C] hover:text-[#8B5A2B]'
                }`}
                data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Inquiry List Button */}
            <Button
              variant="ghost"
              onClick={openDrawer}
              className="relative font-sans text-sm uppercase tracking-widest text-[#2C2C2C] hover:text-[#8B5A2B] hover:bg-transparent px-2"
              data-testid="inquiry-list-btn"
            >
              <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#8B5A2B] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-sans">
                  {itemCount}
                </span>
              )}
            </Button>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="font-sans text-sm uppercase tracking-widest text-[#2C2C2C] hover:text-[#8B5A2B] hover:bg-transparent px-2"
                  data-testid="language-switcher"
                >
                  {language.toUpperCase()}
                  <ChevronDown className="ml-1 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#F9F8F6] border-[#E5E5E5]">
                {Object.values(LANGUAGES).map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'text-[#8B5A2B]' : 'text-[#2C2C2C]'}`}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Inquiry List Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={openDrawer}
              className="relative"
              data-testid="mobile-inquiry-list-btn"
            >
              <ShoppingBag className="h-6 w-6 text-[#2C2C2C]" strokeWidth={1.5} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#8B5A2B] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-sans">
                  {itemCount}
                </span>
              )}
            </Button>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                  <Menu className="h-6 w-6 text-[#2C2C2C]" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#F9F8F6] border-[#E5E5E5] w-[280px]">
                <div className="flex flex-col gap-8 mt-12">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsOpen(false)}
                      className={`font-sans text-lg uppercase tracking-widest transition-colors ${
                        isActive(link.to) 
                          ? 'text-[#8B5A2B]' 
                          : 'text-[#2C2C2C] hover:text-[#8B5A2B]'
                      }`}
                      data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  
                  {/* Mobile Language Switcher */}
                  <div className="pt-8 border-t border-[#E5E5E5]">
                    <p className="text-sm text-[#5D5D5D] uppercase tracking-widest mb-4">Language</p>
                    <div className="flex gap-4">
                      {Object.values(LANGUAGES).map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setIsOpen(false);
                          }}
                          className={`font-sans text-sm uppercase tracking-widest transition-colors ${
                            language === lang.code 
                              ? 'text-[#8B5A2B]' 
                              : 'text-[#2C2C2C] hover:text-[#8B5A2B]'
                          }`}
                          data-testid={`mobile-lang-${lang.code}`}
                        >
                          {lang.code.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
}
